import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavourites, type FavouriteProduct } from '@/hooks/useFavourites';

interface FavouritesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FavouritesDrawer({ isOpen, onClose }: FavouritesDrawerProps) {
  const navigate = useNavigate();
  const { favourites, removeFavourite } = useFavourites();

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleViewProduct = (product: FavouriteProduct) => {
    onClose();
    navigate(`/category?cat=${encodeURIComponent(product.category)}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div className="relative w-full max-w-sm bg-white h-full overflow-y-auto shadow-2xl animate-slide-in-right flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50">
              <i className="ri-heart-fill text-red-500 text-lg"></i>
            </div>
            <div>
              <h3 className="text-gray-900 font-bold text-base">My Favourites</h3>
              <p className="text-gray-400 text-xs">{favourites.length} saved item{favourites.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 py-4">
          {favourites.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center px-6">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <i className="ri-heart-line text-3xl text-red-300"></i>
              </div>
              <h4 className="text-gray-800 font-bold text-lg mb-2">No Favourites Yet</h4>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                Tap the heart icon on any product to save it here for later.
              </p>
              <button
                onClick={() => { onClose(); navigate('/category'); }}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line mr-2"></i>
                Browse Products
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {favourites.map(product => (
                <div
                  key={product.id}
                  className="flex gap-3 bg-white border border-gray-100 rounded-2xl p-3 hover:border-pink-200 transition-all group"
                >
                  {/* Image */}
                  <button
                    onClick={() => handleViewProduct(product)}
                    className="w-20 h-24 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 cursor-pointer"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <p className="text-gray-400 text-[10px] uppercase tracking-wider">{product.category}</p>
                      <h4 className="text-gray-900 font-semibold text-sm leading-tight mt-0.5">{product.name}</h4>
                      <div className="flex items-baseline gap-1.5 mt-1">
                        <span className="text-gray-900 font-bold text-base">₹{product.price}</span>
                        <span className="text-gray-400 text-xs line-through">₹{product.originalPrice}</span>
                        <span className="text-green-600 text-[10px] font-bold">
                          {Math.round((1 - product.price / product.originalPrice) * 100)}% off
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleViewProduct(product)}
                        className="flex-1 py-1.5 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                      >
                        <i className="ri-eye-line text-xs"></i>
                        View
                      </button>
                      <button
                        onClick={() => removeFavourite(product.id)}
                        className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 transition-all cursor-pointer"
                        title="Remove from favourites"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {favourites.length > 0 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 py-4">
            <button
              onClick={() => { onClose(); navigate('/category'); }}
              className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-line mr-2"></i>
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
